import React, { useEffect, useMemo, useRef, useState } from 'react';
import './Panel.scss';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAccount } from '../../../contexts/account/AccountContexts';
import Loader from '../loader/Loader';
import { useTranslation } from 'react-i18next';
import {
  ADS,
  AGENCY_STATS,
  AGENCY_HUB,
  AI_SIGNALS,
  AI_VISIBILITY_REPORT,
  ALL,
  CONTEXT_ANALYSIS,
  BOTS_ACTIVITY,
  BOT_ACCESS,
  FAQ,
  GEO_CHECKLIST,
  GEO_REPORT,
  INSIGHTS,
  INTEGRATIONS,
  KEYWORDS,
  MARKETPLACE,
  PRICING,
  PROJECTS,
  PROMPTS,
  PROMPT_ANALYST,
  REPORTS,
  SOURCES,
  SUGGESTED_PROMPTS,
  TAGS,
} from '../../../routes/path';
import { useGetCloudflareStatus } from '../../../api/hooks/bot-sources/useGetCloudflareStatus';
// Fastly integration is temporarily hidden from clients.
// import { useGetFastlyStatus } from '../../../api/hooks/bot-sources/useGetFastlyStatus';
import { useGetLogUploads } from '../../../api/hooks/bot-sources/useGetLogUploads';
import { ProjectResponse } from '../../../models/projects/ProjectResponse';
import LoginBox from '../../login-box/LoginBox';
import emptyProjectIcon from '../../../../assets/icons/empty-project.svg';
import { Button } from 'primereact/button';
import useDisabledAccount from '../../../hooks/account/UseDisabledAccount';
import useBilling from '../../../hooks/account/UseBilling';
import usePermission from '../../../hooks/global/UsePermission';
import CustomTooltip from '../tooltip/CustomTooltip';
import { getProjectsFromAccount } from '../../../helpers/project/ProjectHelper';
import { PRIVACY_POLICY } from '../../../utils/urls';
import { isAdminOrOwner } from '../../../utils/utils';
import { buildProjectMenu, findActiveMenuGroup, ProjectMenuItem } from './helpers/ProjectMenuHelper';
import { canUseVisibilityReport } from '../../../helpers/addons/canUseVisibilityReport';
import { promptAnalystAccess } from '../../../helpers/addons/promptAnalystAccess';
import { useModuleAccess } from '../../../hooks/global/UseModuleAccess';
import useStickyObserver from './hooks/UseStickyObserver';
import SimpleBar from 'simplebar-react';
import AdvisorBox from '../advisor-box/AdvisorBox';
import MasterclassBox from '../masterclass-box/MasterclassBox';
import { useProjectGroups } from '../../../hooks/projects/UseProjectGroups';
import { useGetProject } from '../../../api/hooks/project/useGetProject';

interface PanelProps {
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

const Panel: React.FC<PanelProps> = ({ isCollapsed = false, onToggleCollapse }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const { isDisabled } = useDisabledAccount();
  // The Settings "Subscription" tab's rule: the billing addon AND read access to BILLING.
  const billingAccess = usePermission('BILLING');
  const canShowBilling = useBilling() && billingAccess.read;

  const { accountResponse, projectResponse, setShowVideoTutorial } = useAccount();
  const isGuest = accountResponse?.role === 'GUEST';
  const [isOpen, setIsOpen] = useState(false);
  const [favicons, setFavicons] = useState<Record<string, string>>({});
  const [projects, setProjects] = useState<ProjectResponse[]>([]);
  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
  const [collapsedDropdownGroups, setCollapsedDropdownGroups] = useState<Set<number>>(new Set());
  // Świadomie BEZ zapamiętywania między wejściami: menu zaczyna zwinięte, a jedyne, co je otwiera,
  // to wejście na stronę z danej kategorii. Zapisany stan po kilku sesjach kończy się rozwiniętym
  // wszystkim, czyli dokładnie tą długą listą, którą kategorie miały uporządkować.
  const [openMenuGroups, setOpenMenuGroups] = useState<Set<string>>(() => new Set());
  const submenuRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(
    null
  );

  const { data } = useProjectGroups();
  const projectGroups = data ?? [];

  useEffect(() => {
    document.body.classList.remove('visible', 'white');
  }, []);
  useStickyObserver();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        submenuRef.current &&
        !submenuRef.current.contains(event.target as Node)
      ) {
        setIsSubmenuOpen(false);
      }
    };

    if (isSubmenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSubmenuOpen]);

  // Close dropdown on panel scroll
  useEffect(() => {
    if (!isSubmenuOpen || !submenuRef.current) return;
    const panelMenu = submenuRef.current.closest('.panel__menu');
    if (!panelMenu) return;
    const handleScroll = () => setIsSubmenuOpen(false);
    panelMenu.addEventListener('scroll', handleScroll);
    return () => panelMenu.removeEventListener('scroll', handleScroll);
  }, [isSubmenuOpen]);

  useEffect(() => {
    const newFavicons: Record<string, string> = {};

    projects.forEach((project) => {
      if (!favicons[project.domain]) {
        newFavicons[project.domain] = project.domain
          ? `https://favicon.is/${project.domain}`
          : emptyProjectIcon;
      }
    });

    setFavicons((prev) => ({ ...prev, ...newFavicons }));
  }, [projects]);

  useEffect(() => {
    if (!accountResponse || !projectResponse) {
      return;
    }

    const filteredProjects = getProjectsFromAccount(accountResponse, projectResponse);
    setProjects(filteredProjects);
  }, [accountResponse, projectResponse]);

  // For the sidebar "Bots activity" link we want to skip the connect-page bounce
  // when the project already has a data source. These two cheap reads (cached
  // 30 s / 1 min) are shared with the BotsActivity pages, so they don't
  // double-fetch when the user is actually in that area. Disabled when there is
  // no current project.
  const currentProjectId = useMemo(() => {
    // Match `<route>/:projectId` for any project-scoped route. Route paths come
    // from the shared `path` module so the list stays in sync with the router.
    const escapeForRegex = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const routes = [
      PROJECTS,
      SUGGESTED_PROMPTS,
      TAGS,
      KEYWORDS,
      PROMPTS,
      SOURCES,
      ADS,
      INSIGHTS,
      CONTEXT_ANALYSIS,
      GEO_CHECKLIST,
      BOTS_ACTIVITY,
      BOT_ACCESS,
      AI_SIGNALS,
      AI_VISIBILITY_REPORT,
      PROMPT_ANALYST,
      REPORTS,
    ];
    const patterns = routes.map((r) => new RegExp(`^${escapeForRegex(r)}/(\\d+)`));

    for (const pattern of patterns) {
      const match = location.pathname.match(pattern);
      if (match) {
        return Number(match[1]);
      }
    }

    return null;
  }, [location.pathname]);

  // Gość nie ma pozycji „Bots activity" w menu, więc sondowanie źródeł logów byłoby tylko serią 403
  // na każdym wejściu w projekt. `null` wyłącza oba zapytania.
  const botsActivityProbeId = isGuest ? null : currentProjectId ?? null;
  const botsActivityCloudflareStatus = useGetCloudflareStatus(botsActivityProbeId);
  // Fastly integration is temporarily hidden from clients.
  // const botsActivityFastlyStatus = useGetFastlyStatus(botsActivityProbeId);
  const botsActivityLogUploads = useGetLogUploads(botsActivityProbeId);
  // Probe every active source — if any one is connected, the "Bots activity"
  // sidebar link goes straight to the analysis page instead of bouncing through
  // the connect page. Skipping a provider here causes a visible redirect flash
  // for users connected to that source.
  const botsActivityHasAnyDataSource =
    !!botsActivityCloudflareStatus.data?.isConnected ||
    // !!botsActivityFastlyStatus.data?.isConnected ||
    botsActivityLogUploads.data.length > 0;

  const currentProject = useMemo(() => {
    if (!currentProjectId) {
      return null;
    }

    const projectsToSearch =
      accountResponse && accountResponse.isLimitedSuperAdmin ? projectResponse || projects : projects;
    const found = projectsToSearch.find((p) => p.id === currentProjectId);
    if (found) return found;

    // The URL has a project id but the loaded project list does not contain it
    // (superadmin opening a project outside their account, or a brief render
    // before `projects` is populated). Returning `null` would hide the entire
    // project section in the sidebar, including the Bots Activity / Bot Access nav links,
    // even though we know which project the user is on. Fall back to a stub so
    // the submenu renders with sane defaults — the labels are project-agnostic
    // and the links use the id directly.
    return {
      id: currentProjectId,
      projectLabel: `Project ${currentProjectId}`,
      domain: '',
      defaultPeriod: 'DAILY',
      demoVersion: null,
      status: 'ACTIVE',
    } as ProjectResponse;
  }, [currentProjectId, projects, projectResponse, accountResponse]);

  // Addony WŁAŚCICIELA projektu, nie zalogowanego konta. `currentProject` bywa atrapą (superadmin
  // wchodzi na cudzy projekt, którego nie ma na swojej liście), więc `ownerAddons` bierzemy z
  // endpointu projektu — to samo zapytanie, które robią już strony raportu, więc leci z cache.
  const { project: currentProjectDetails } = useGetProject(currentProjectId ?? null);
  const effectiveAddons = (currentProjectDetails?.ownerAddons ??
    accountResponse?.addons ??
    []) as string[];
  const showVisibilityReport = canUseVisibilityReport(effectiveAddons, !!accountResponse?.isSuperAdmin);
  // Zakładka widoczna wyłącznie dzięki uprawnieniom superadmina — właściciel projektu nie ma
  // dodatku, więc oznaczamy ją, żeby nie brać jej za coś, co widzi klient.
  const isVisibilityReportDevOnly =
    showVisibilityReport && !effectiveAddons.includes('VISIBILITY_REPORT');
  // Ads gates on the LOGGED-IN account's addon, like the legacy menu entries: the addon is always
  // required — the super-admin role only widens the reach (all projects), it unlocks nothing by itself.
  const hasAdsAddon = !!accountResponse?.addons?.includes('ADS_STATS');
  // Module gate rule (docs/frontend-conventions.md → "Module gating"): owner's addon OR super admin.
  const insightsAccess = useModuleAccess('INSIGHTS', currentProjectId ?? null, {
    allowSuperAdmin: true,
  });

  // Submenu projektu jako dane — kolejność, ikony i warunki widoczności siedzą w `ProjectMenuHelper`.
  const projectMenu = useMemo(
    () =>
      currentProject && accountResponse
        ? buildProjectMenu({
            project: currentProject,
            addons: accountResponse.addons,
            showVisibilityReport,
            isVisibilityReportDevOnly,
            hasAdsAddon,
            insights: { canAccess: insightsAccess.canAccess, isDevOnly: insightsAccess.isDevOnly },
            promptAnalyst: promptAnalystAccess(accountResponse),
            botsActivityHasAnyDataSource,
            isGuest,
          })
        : [],
    [
      currentProject,
      accountResponse,
      showVisibilityReport,
      isVisibilityReportDevOnly,
      hasAdsAddon,
      insightsAccess.canAccess,
      insightsAccess.isDevOnly,
      botsActivityHasAnyDataSource,
      isGuest,
    ]
  );

  const activeMenuGroup = useMemo(
    () => findActiveMenuGroup(projectMenu, location.pathname),
    [projectMenu, location.pathname]
  );

  // Grupa z bieżącą stroną rozwija się sama — inaczej po wejściu z adresu aktywna pozycja siedziałaby
  // w zwiniętej grupie i nie byłoby po menu widać, gdzie się jest.
  //
  // Zależność to SAM klucz grupy, a nie ścieżka: dzięki temu przejście między stronami tej samej
  // kategorii nie odpala efektu, więc grupa ręcznie zwinięta w trakcie pracy zostaje zwinięta.
  useEffect(() => {
    if (!activeMenuGroup) {
      return;
    }
    setOpenMenuGroups((prev) => (prev.has(activeMenuGroup) ? prev : new Set(prev).add(activeMenuGroup)));
  }, [activeMenuGroup]);

  const toggleMenuGroup = (key: string) => {
    setOpenMenuGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const hasProjectGroupsAddon = !!accountResponse?.addons?.includes('PROJECT_GROUPS');
  const sortedProjectGroups = useMemo(
    () => [...projectGroups].sort((a, b) => a.position - b.position),
    [projectGroups]
  );

  const toggleSubmenu = (e: React.MouseEvent) => {
    if (!isSubmenuOpen) {
      // When opening, collapse all groups except the current project's group.
      // Ungrouped projects are rendered flat (no collapsible section) so no sentinel
      // is added here.
      const currentGroupId = currentProject?.projectGroupId;
      const allGroupIds = new Set(sortedProjectGroups.map((g) => g.id));
      if (currentGroupId !== null && currentGroupId !== undefined) {
        allGroupIds.delete(currentGroupId);
      }
      setCollapsedDropdownGroups(allGroupIds);

      // Match original position: absolute; top: 42px; left: 33px; width: 100% relative to submenuRef
      if (submenuRef.current) {
        const rect = submenuRef.current.getBoundingClientRect();
        setDropdownPos({ top: rect.top + 42, left: rect.left + 33, width: rect.width });
      }
    }
    setIsSubmenuOpen(!isSubmenuOpen);
  };

  const renderCurrentProject = () => {
    if (!currentProject || !accountResponse) {
      return null;
    }

    const handleSubmenuItemClick = () => {
      setIsSubmenuOpen(false);
    };

    /**
     * `isTopLevel` dotyczy pozycji bez kategorii (Overview, Ads). Stoją w jednym rzędzie z nagłówkami
     * grup, więc mają wyglądać jak one, a nie jak pozycja wcięta pod grupą.
     */
    const renderMenuItem = (item: ProjectMenuItem, isTopLevel = false) => {
      const domId = `${item.id}-${currentProject.id}`;
      return (
        <React.Fragment key={item.id}>
          <NavLink
            id={domId}
            to={item.to}
            className={({ isActive }) =>
              (isActive ? 'menu__item-small--active' : '') +
              ' menu__item-small' +
              (isTopLevel ? ' menu__row-top' : '')
            }
            onClick={handleSubmenuItemClick}
          >
            <i className={`${item.icon} ${isTopLevel ? 'ico--sm' : 'ico--xs'}`} />
            {item.badge ? (
              <div className="indicator-container">
                <span className="menu__text">{t(item.labelKey)}</span>
                <div className="indicator-container__right">
                  <span className="indicator indicator--beta">{item.badge}</span>
                </div>
              </div>
            ) : (
              <span className="menu__text">{t(item.labelKey)}</span>
            )}
          </NavLink>
          <CustomTooltip
            tooltipText={item.labelKey}
            targetRef={domId}
            isMobileTooltip={true}
            direction="right"
          />
        </React.Fragment>
      );
    };

    return (
      <div key={`project-submenu-${currentProject.id}`}>
        <div
          id={`project-btn-${currentProject.id}`}
          className="menu__item--active menu__item menu__project-btn"
        >
          <div className="menu__item-text-container">
            <span
              style={{
                backgroundImage: `url(${favicons[currentProject.domain]})`,
              }}
            />
            <span className="menu__text">
              {currentProject.projectLabel} {currentProject.demoVersion && '(Demo)'}
            </span>
          </div>
          {/* Strzałka rozwija listę projektów do przełączania. Przy jednym projekcie nie ma na co
              przełączać — otwierałaby dropdown z jedną pozycją, tą samą, która stoi wyżej. */}
          {projects.length > 1 && (
            <div className="menu__item-navigation">
              <button
                className={`menu__item-nav-button ${isSubmenuOpen ? 'menu__item-nav-button--active' : ''}`}
                onClick={toggleSubmenu}
              >
                <span className={`${isSubmenuOpen ? 'ico--rotate' : ''} ico--bottom ico--sm`} />
              </button>
            </div>
          )}
        </div>

        <div className="menu__list menu__list--open">
          {projectMenu.map((entry) =>
            entry.kind === 'item' ? (
              renderMenuItem(entry.item, true)
            ) : (
              <div key={entry.group.key} className="menu__group">
                <button
                  type="button"
                  id={`group-${entry.group.key}-btn`}
                  className="menu__group-header menu__row-top"
                  aria-expanded={openMenuGroups.has(entry.group.key)}
                  onClick={() => toggleMenuGroup(entry.group.key)}
                >
                  <i className={`${entry.group.icon} ico--sm`} />
                  <span className="menu__text">{t(entry.group.labelKey)}</span>
                  <span
                    className={`ico--bottom ico--sm menu__group-chevron${
                      openMenuGroups.has(entry.group.key) ? ' menu__group-chevron--open' : ''
                    }`}
                  />
                </button>
                {/* W wąskim panelu z nagłówka zostaje sama ikona — bez tego nie da się poznać, co
                    kryje kategoria. Tooltipy pozycji menu i tak pokazują się wyłącznie tam. */}
                <CustomTooltip
                  tooltipText={entry.group.labelKey}
                  targetRef={`group-${entry.group.key}-btn`}
                  isMobileTooltip={true}
                  direction="right"
                />
                <div
                  className={`menu__group-content${
                    openMenuGroups.has(entry.group.key) ? ' menu__group-content--open' : ''
                  }`}
                >
                  <div className="menu__group-content-inner">
                    {entry.group.items.map((groupItem) => renderMenuItem(groupItem))}
                  </div>
                </div>
              </div>
            )
          )}
        </div>
        {isSubmenuOpen && dropdownPos && (
          <div
            className="menu__projects-dropdown"
            ref={dropdownRef}
            style={{
              position: 'fixed',
              top: dropdownPos.top,
              left: dropdownPos.left,
              width: dropdownPos.width,
              zIndex: 1000,
            }}
          >
            <SimpleBar
              className="menu__projects-dropdown-list"
              style={{ maxHeight: 252 }}
              autoHide={false}
            >
              {hasProjectGroupsAddon && sortedProjectGroups.length > 0 ? (
                <>
                  {sortedProjectGroups.map((group) => {
                    const groupProjects = projects.filter((p) => p.projectGroupId === group.id);
                    if (groupProjects.length === 0) return null;
                    const isCollapsed = collapsedDropdownGroups.has(group.id);

                    return (
                      <div key={`dropdown-group-${group.id}`} className="menu__dropdown-group">
                        <div
                          className={`menu__dropdown-group-header${
                            !collapsedDropdownGroups.has(group.id)
                              ? ' menu__dropdown-group-header--active'
                              : ''
                          }${
                            currentProject?.projectGroupId === group.id
                              ? ' menu__dropdown-group-header--current'
                              : ''
                          }`}
                          onClick={() => {
                            setCollapsedDropdownGroups((prev) => {
                              const next = new Set(prev);
                              if (next.has(group.id)) next.delete(group.id);
                              else next.add(group.id);
                              return next;
                            });
                          }}
                        >
                          <span
                            className="menu__dropdown-group-dot"
                            style={{ backgroundColor: group.color || 'var(--grey-ux-400)' }}
                          />
                          <span className="menu__dropdown-group-name">{group.name}</span>
                          <span
                            className={`ico--up ico--xs menu__dropdown-group-chevron${
                              !isCollapsed ? ' menu__dropdown-group-chevron--expanded' : ''
                            }`}
                          />
                        </div>
                        <div
                          className={`menu__dropdown-group-content${
                            !isCollapsed ? ' menu__dropdown-group-content--open' : ''
                          }`}
                        >
                          <div className="menu__dropdown-group-content-inner">
                            {groupProjects.map((project) => (
                              <NavLink
                                key={`project-submenu-btn-${project.id}`}
                                to={`${PROJECTS}/${
                                  project.id
                                }/${project.defaultPeriod.toLowerCase()}${ALL}`}
                                className={`menu__item${
                                  project.id === currentProject?.id ? ' menu__item--active' : ''
                                }`}
                                onClick={toggleSubmenu}
                              >
                                <div className="menu__item-text-container">
                                  <span
                                    style={{
                                      backgroundImage: `url(${favicons[project.domain]})`,
                                    }}
                                  />
                                  <span className="menu__text">
                                    {project.projectLabel} {project.demoVersion && '(Demo)'}
                                  </span>
                                </div>
                              </NavLink>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {projects
                    .filter((p) => !p.projectGroupId)
                    .map((project) => (
                      <NavLink
                        key={`project-submenu-btn-${project.id}`}
                        to={`${PROJECTS}/${project.id}/${project.defaultPeriod.toLowerCase()}${ALL}`}
                        className={`menu__item${
                          project.id === currentProject?.id ? ' menu__item--active' : ''
                        }`}
                        onClick={toggleSubmenu}
                      >
                        <div className="menu__item-text-container">
                          <span
                            style={{
                              backgroundImage: `url(${favicons[project.domain]})`,
                            }}
                          />
                          <span className="menu__text">
                            {project.projectLabel} {project.demoVersion && '(Demo)'}
                          </span>
                        </div>
                      </NavLink>
                    ))}
                </>
              ) : (
                projects.map((project) => (
                  <NavLink
                    key={`project-submenu-btn-${project.id}`}
                    to={`${PROJECTS}/${project.id}/${project.defaultPeriod.toLowerCase()}${ALL}`}
                    className={`menu__item${
                      project.id === currentProject?.id ? ' menu__item--active' : ''
                    }`}
                    onClick={toggleSubmenu}
                  >
                    <div className="menu__item-text-container">
                      <span
                        style={{
                          backgroundImage: `url(${favicons[project.domain]})`,
                        }}
                      />
                      <span className="menu__text">
                        {project.projectLabel} {project.demoVersion && '(Demo)'}
                      </span>
                    </div>
                  </NavLink>
                ))
              )}
            </SimpleBar>
          </div>
        )}
      </div>
    );
  };

  if (!accountResponse || !projects || !projectResponse) {
    return <Loader />;
  }

  return (
    <div className={`panel ${isCollapsed ? 'panel--small' : ''}`}>
      <div className="logo">
        <NavLink to={PROJECTS}>
          <div className="logo-box logo-box--app" />
        </NavLink>
        {onToggleCollapse && (
          <>
            <Button
              id="btn-toggle-panel"
              className="action-btn panel__collapse-btn"
              onClick={onToggleCollapse}
            >
              <span className="ico--right ico--sm" />
            </Button>
            <CustomTooltip
              tooltipText={isCollapsed ? 'expandMenu' : 'collapseMenu'}
              targetRef="btn-toggle-panel"
            />
          </>
        )}
      </div>
      <div className="panel__menu-panel">
        <div className="panel__menu menu">
          {!isDisabled && currentProject && (
            <>
              <div className="menu__separator" />
              <div className="panel__menu-title">{t('project')}</div>
              <div className="menu__submenu" ref={submenuRef}>
                {renderCurrentProject()}
              </div>
            </>
          )}

          <div className="menu__separator" />
          <div className="panel__menu-title">{t('tools')}</div>
          <div className="menu__tools">
            {!isDisabled && (
              <>
                <NavLink
                  id="projects-btn"
                  to={PROJECTS}
                  className={({ isActive }) => {
                    const isExactProjects = location.pathname === PROJECTS;
                    return (isExactProjects ? 'menu__item--active' : '') + ' menu__item';
                  }}
                >
                  <i className="ico--bookmark ico--sm" />
                  <span className="menu__text">{t('allProjects')}</span>
                </NavLink>
                <CustomTooltip
                  tooltipText={t('allProjects')}
                  targetRef="projects-btn"
                  isMobileTooltip={true}
                  direction="right"
                />
              </>
            )}
            {/* Gość ma w menu tylko Projekty, więc FAQ przenosi się tu z `LoginBox` — jedyna
                pozycja, która dotyczy jego samego, a nie cudzego konta. */}
            {isGuest && !isDisabled && (
              <>
                <NavLink
                  id="faq-btn"
                  to={FAQ}
                  className={({ isActive }) => (isActive ? 'menu__item--active' : '') + ' menu__item'}
                >
                  <i className="ico--chat ico--sm" />
                  <span className="menu__text">{t('faq')}</span>
                </NavLink>
                <CustomTooltip
                  tooltipText={t('faq')}
                  targetRef="faq-btn"
                  isMobileTooltip={true}
                  direction="right"
                />
              </>
            )}
            {/* Menu główne gościa to same Projekty (+ FAQ wyżej). Reszta dotyczy konta, na którym
                jest tylko widzem (rozliczenia, integracje, statystyki agencji), albo to materiały
                dla jego właściciela (video, advisor, masterclass). */}
            {!isGuest && (
              <>
                {accountResponse.addons.includes('GEO_REPORT') && !isDisabled && (
                  <>
                    <NavLink
                      id="geo-audit-btn"
                      to={GEO_REPORT}
                      className={({ isActive }) =>
                        (isActive ? 'menu__item--active' : '') + ' menu__item'
                      }
                    >
                      <i className="ico--report ico--sm" />
                      <span className="menu__text">{t('geoReport.navLabel')}</span>
                    </NavLink>
                    <CustomTooltip
                      tooltipText={t('geoReport.navLabel')}
                      targetRef="geo-audit-btn"
                      isMobileTooltip={true}
                      direction="right"
                    />
                  </>
                )}
                {accountResponse.addons.includes('AGENCY') && (
                  <>
                    <NavLink
                      id="agency-stats-btn"
                      to={AGENCY_STATS}
                      className={({ isActive }) =>
                        (isActive ? 'menu__item--active' : '') + ' menu__item'
                      }
                    >
                      <i className="ico--chart ico--sm" />
                      <span className="menu__text">{t('agencyStats')}</span>
                    </NavLink>
                    <CustomTooltip
                      tooltipText={t('agencyStats')}
                      targetRef="agency-stats-btn"
                      isMobileTooltip={true}
                      direction="right"
                    />
                  </>
                )}
                {accountResponse.addons.includes('AGENCY_HUB') &&
                  isAdminOrOwner(accountResponse.role) && (
                    <>
                      <NavLink
                        id="agency-hub-btn"
                        to={AGENCY_HUB}
                        className={({ isActive }) =>
                          (isActive ? 'menu__item--active' : '') + ' menu__item'
                        }
                      >
                        <i className="ico--chart ico--sm" />
                        <span className="menu__text">{t('agencyHub')}</span>
                      </NavLink>
                      <CustomTooltip
                        tooltipText={t('agencyHub')}
                        targetRef="agency-hub-btn"
                        isMobileTooltip={true}
                        direction="right"
                      />
                    </>
                  )}
                {(accountResponse.addons.includes('LOOKER_STUDIO') ||
                  accountResponse.addons.includes('AI_SIGNALS')) && (
                  <>
                    <NavLink
                      id="looker-studio-btn"
                      to={`${INTEGRATIONS}`}
                      className={({ isActive }) =>
                        (isActive ? 'menu__item--active' : '') + ' menu__item'
                      }
                    >
                      <i className="ico--puzzle ico--sm" />
                      <span className="menu__text">{t('integrations')}</span>
                    </NavLink>
                    <CustomTooltip
                      tooltipText={t('integrations')}
                      targetRef="looker-studio-btn"
                      isMobileTooltip={true}
                      direction="right"
                    />
                  </>
                )}
                {accountResponse.addons.includes('MARKETPLACE') && (
                  <>
                    <NavLink
                      id="marketplace-btn"
                      to={MARKETPLACE}
                      className={({ isActive }) =>
                        (isActive ? 'menu__item--active' : '') + ' menu__item'
                      }
                    >
                      <i className="ico--shopping-cart ico--sm" />
                      <span className="menu__text">{t('marketplace')}</span>
                    </NavLink>
                    <CustomTooltip
                      tooltipText={t('marketplace')}
                      targetRef="marketplace-btn"
                      isMobileTooltip={true}
                      direction="right"
                    />
                  </>
                )}
                {canShowBilling && (
                  <>
                    <NavLink
                      id="subscription-btn"
                      to={PRICING}
                      className={({ isActive }) =>
                        (isActive ? 'menu__item--active' : '') + ' menu__item'
                      }
                    >
                      <i className="ico--card ico--sm" />
                      <span className="menu__text">{t('subscription')}</span>
                    </NavLink>
                    <CustomTooltip
                      tooltipText={t('subscription')}
                      targetRef="subscription-btn"
                      isMobileTooltip={true}
                      direction="right"
                    />
                  </>
                )}
                <Button id="video-btn" onClick={() => setShowVideoTutorial(true)} className="menu__item">
                  <i className="ico--video ico--sm" />
                  <span className="menu__text">{t('videoTutorial')}</span>
                </Button>
                <CustomTooltip
                  tooltipText={t('videoTutorial')}
                  targetRef="video-btn"
                  isMobileTooltip={true}
                  direction="right"
                />
                <AdvisorBox />
                <MasterclassBox />
              </>
            )}
          </div>
        </div>
        <div className="panel__end">
          <div className="panel__footer">
            <LoginBox />
          </div>
          <ul className="privacy-policy">
            <li>
              <Link to={PRIVACY_POLICY} target="_blank">
                {t('privacyPolicy')}
              </Link>
            </li>
            <li>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  (window as any).OneTrust?.ToggleInfoDisplay();
                }}
              >
                {t('customizeCookie')}
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Panel;
